import { openModal, showSpinner, hideSpinner } from "@sitecorecomponents/BasicContent/Modal";
import { cprFormatRule } from "../formValidation";
import { showFormMessage } from "../formMessages";
import { fetchApi, fetchApiGraphQL, type GraphQLError } from '@/utils/fetcher';
import {
  getAffiliationFieldsMutation,
  type GetAffiliationFieldsResult,
  type AffiliationField,
  addUserAffiliationMutation,
  type AddUserAffiliationResult,
  updateUserAffiliationMutation,
  type UpdateUserAffiliationResult,
  updateUserCprMutation,
  type UpdateUserCprResult,
  getUserProfileMutation,
  type GetUserProfileResult,
  uploadDocumentUrl,
  type UploadDocumentResult
} from "./affiliation.query";
import { affiliationTypes, checkAffiliationLimit, type Affiliation } from "./affiliationStore";

export type OpenAffiliationOptions = {
  affiliation?: Affiliation;
  onSaved: () => Promise<void>;
};

type FieldInput = {
  input: string;
  kind: 'text' | 'cpr' | 'file';
};

const fieldInputs: Record<string, FieldInput> = {
  memberCprNumber: { input: 'MemberCprNumber', kind: 'cpr' },
  studentProgram: { input: 'StudyDirection', kind: 'text' },
  studentEndDate: { input: 'EducationEndDate', kind: 'text' },
  studyCheckDocument: { input: 'StudyScheduleDocumentation', kind: 'file' },
  proofOfPaidUnionMembership: { input: 'UnionReceiptDocumentation', kind: 'file' },
};

const ownCprInput = 'CprNumber';

function typeFields(affiliationType: string): string[] {
  return affiliationTypes[affiliationType]?.fields ?? [];
}

function inputFor(form: HTMLFormElement, id: string) {
  return form.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
}

function selectedType(form: HTMLFormElement): string {
  return form.querySelector<HTMLSelectElement>('#AffiliationType')?.value ?? '';
}

function pickedFile(form: HTMLFormElement, key: string): File | undefined {
  return form.querySelector<HTMLInputElement>(`#${fieldInputs[key].input}`)?.files?.[0];
}

function normalizeCpr(value: string): string {
  return value.replace(/-/g, '');
}

function collectFields(form: HTMLFormElement, affiliationType: string): Record<string, string> {
  const fields: Record<string, string> = {};

  for (const key of typeFields(affiliationType)) {
    const field = fieldInputs[key];
    if (field.kind === 'file') {
      continue;
    }

    const value = inputFor(form, field.input)?.value.trim() ?? '';
    fields[key] = field.kind === 'cpr' ? normalizeCpr(value) : value;
  }

  return fields;
}

function missingRequired(
    affiliationType: string,
    fields: Record<string, string>
): boolean {
  return typeFields(affiliationType).some(key =>
      fieldInputs[key].kind !== 'file' && !fields[key]);
}

function invalidCprField(affiliationType: string, fields: Record<string, string>): string | undefined {
  return typeFields(affiliationType).find(
    key => fieldInputs[key].kind === 'cpr' && !cprFormatRule.test(fields[key] ?? '')
  );
}

function nothingChanged(
  form: HTMLFormElement,
  affiliationType: string,
  fields: Record<string, string>,
  existing?: Affiliation,
): boolean {
  if (!existing?.affiliationId || existing.affiliationType !== affiliationType) {
    return false;
  }

  return typeFields(affiliationType).every(key =>
    fieldInputs[key].kind === 'file'
      ? !pickedFile(form, key)
      : fields[key] === (existing.fields[key] ?? '')
  );
}

class DocumentUploadError extends Error {}

async function uploadDocument(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchApi(uploadDocumentUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new DocumentUploadError(`${file.name}: ${await response.text()}`);
  }

  const result = await response.json() as UploadDocumentResult;
  return result.documentId;
}

async function resolveDocuments(
  form: HTMLFormElement,
  affiliationType: string,
  existing?: Affiliation,
): Promise<Record<string, string>> {
  const documentKeys = typeFields(affiliationType).filter(key => fieldInputs[key].kind === 'file');

  const ids = await Promise.all(documentKeys.map(key => {
    const file = pickedFile(form, key);
    return file ? uploadDocument(file) : Promise.resolve(existing?.fields[key] ?? '');
  }));

  return Object.fromEntries(documentKeys.map((key, index) => [key, ids[index]]).filter(([, id]) => id));
}

async function submitAffiliation(
  editingId: string | undefined,
  affiliationType: string,
  fields: Record<string, string>,
): Promise<boolean> {
  const entries = Object.entries(fields).map(([key, value]) => ({ key, value }));
  const variables = { affiliationType, fields: entries.length ? entries : undefined };

  if (editingId) {
    const result = await fetchApiGraphQL<UpdateUserAffiliationResult>(
      updateUserAffiliationMutation,
      { affiliationId: editingId, ...variables }
    );
    return !!result?.data?.updateUserAffiliation;
  }

  const result = await fetchApiGraphQL<AddUserAffiliationResult>(
    addUserAffiliationMutation,
    variables
  );
  return !!result?.data?.addUserAffiliation;
}

async function loadProfileCpr(): Promise<string | null> {
  try {
    const result = await fetchApiGraphQL<GetUserProfileResult>(getUserProfileMutation, {});
    return result?.data?.getUserProfile?.cprNumber ?? null;
  } catch {
    return null;
  }
}

async function saveOwnCpr(form: HTMLFormElement): Promise<string | null> {
  const input = form.querySelector<HTMLInputElement>(`#${ownCprInput}`);
  const value = input?.value.trim() ?? '';

  if (!value || value === input?.dataset.loaded) {
    return null;
  }

  if (!cprFormatRule.test(value)) {
    return ownCprInput;
  }

  try {
    await fetchApiGraphQL<UpdateUserCprResult>(updateUserCprMutation, { cprNumber: normalizeCpr(value) });
    return null;
  } catch (err) {
    console.error('Failed to update CPR number', err);
    return ownCprInput;
  }
}

async function autofillOwnCpr(form: HTMLFormElement) {
  if (!affiliationTypes[selectedType(form)]?.ownCpr) {
    return;
  }

  const cpr = await loadProfileCpr();
  const input = form.querySelector<HTMLInputElement>(`#${ownCprInput}`);
  if (cpr && input && !input.value) {
    input.value = cpr;
    input.dataset.loaded = cpr;
  }
}

function populateStudyDirection(form: HTMLFormElement, fields: AffiliationField[], selected?: string) {
  const select = form.querySelector<HTMLSelectElement>(`#${fieldInputs.studentProgram.input}`);
  if (!select) {
    return;
  }

  const previous = select.value;
  const placeholder = select.querySelector('option[value=""]');
  select.innerHTML = '';
  if (placeholder) {
    select.appendChild(placeholder);
  }

  fields.find(f => f.key === 'studentProgram')?.options?.forEach(option => {
    const element = document.createElement('option');
    element.value = option;
    element.textContent = option;
    select.appendChild(element);
  });

  select.value = previous || selected || '';
}

async function loadAffiliationFields(affiliationType: string, form: HTMLFormElement, selected?: string): Promise<void> {
  if (!typeFields(affiliationType).includes('studentProgram')) {
    return;
  }

  try {
    const result = await fetchApiGraphQL<GetAffiliationFieldsResult>(
      getAffiliationFieldsMutation,
      { affiliationType }
    );
    populateStudyDirection(form, result.data.getAffiliationFields ?? [], selected);
  } catch (error) {
    console.error('Failed to load affiliation fields', error);
  }
}

function appliesToAffiliation(affiliationType: string, condition: string | undefined) {
  return condition?.split(' ').includes(affiliationType) ?? false;
}

function toggleAffiliationFields(form: HTMLFormElement, affiliationType: string) {
  form.querySelectorAll<HTMLElement>('[data-affiliation-when]').forEach(el => {
    const matches = appliesToAffiliation(affiliationType, el.dataset.affiliationWhen);
    el.classList.toggle('hidden', !matches);
    el.classList.toggle('flex', matches);
  });

  form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-affiliation-required-when]').forEach(input => {
    const applies = appliesToAffiliation(affiliationType, input.dataset.affiliationRequiredWhen);
    input.required = applies;

    if (!applies) {
      input.value = '';
    }
  });
}

function fillAffiliation(form: HTMLFormElement, affiliation: Affiliation) {
  const select = form.querySelector<HTMLSelectElement>('#AffiliationType');
  if (select) {
    select.value = affiliation.affiliationType;
  }

  for (const key of typeFields(affiliation.affiliationType)) {
    if (fieldInputs[key].kind === 'file') {
      continue;
    }

    const input = inputFor(form, fieldInputs[key].input);
    if (input) {
      input.value = affiliation.fields[key] ?? '';
      input.dataset.loaded = input.value;
    }
  }
}

function showFieldError(form: HTMLFormElement, inputId: string) {
  form.querySelector<HTMLElement>(`[data-validation-message="${inputId}"]`)?.classList.remove('hidden');
  form.querySelector<HTMLInputElement>(`#${inputId}`)?.focus();
}

function clearFieldErrors(form: HTMLFormElement) {
  form.querySelectorAll<HTMLElement>('[data-validation-message]').forEach(el => el.classList.add('hidden'));
}

function setupCprValidation(form: HTMLFormElement, inputId: string) {
  const input = form.querySelector<HTMLInputElement>(`#${inputId}`);
  const message = form.querySelector<HTMLElement>(`[data-validation-message="${inputId}"]`);
  if (!input || !message) {
    return;
  }

  const update = () => {
    const value = input.value.trim();
    const untouched = value === '' || value === input.dataset.loaded;
    message.classList.toggle('hidden', untouched || cprFormatRule.test(value));
  };

  input.addEventListener('input', update);
  input.addEventListener('blur', update);
  update();
}

function setupAffiliationFields(form: HTMLFormElement) {
  const select = form.querySelector<HTMLSelectElement>('#AffiliationType');
  if (!select) {
    return;
  }

  select.addEventListener('change', async () => {
    toggleAffiliationFields(form, select.value);
    await loadAffiliationFields(select.value, form);
    await autofillOwnCpr(form);
  });

  toggleAffiliationFields(form, select.value);
}

function setupFileInputs(form: HTMLFormElement, affiliation?: Affiliation) {
  for (const [key, field] of Object.entries(fieldInputs)) {
    if (field.kind !== 'file') {
      continue;
    }

    const input = form.querySelector<HTMLInputElement>(`#${field.input}`);
    const fileName = form.querySelector<HTMLElement>(`[data-file-name-for="${field.input}"]`);
    if (!input || !fileName) {
      continue;
    }

    if (affiliation?.fields[key]) {
      fileName.textContent = fileName.dataset.uploadedText || 'Fil uploadet';
    }

    const emptyText = fileName.textContent;
    input.addEventListener('change', () => {
      fileName.textContent = input.files?.[0]?.name ?? emptyText;
    });
  }
}

function errorKey(err: unknown): string {
  if (err instanceof DocumentUploadError) {
    return 'affiliation-file-invalid';
  }

  const message = (err as GraphQLError)?.errors?.[0]?.message ?? '';
  return message.includes('already') || message.includes('maximum')
    ? 'affiliation-duplicate'
    : 'affiliation-failed';
}

async function handleSubmit(
  e: SubmitEvent,
  form: HTMLFormElement,
  options: OpenAffiliationOptions,
  dialog: HTMLDialogElement | null,
) {
  e.preventDefault();
  showFormMessage(form, null);
  clearFieldErrors(form);

  const existing = options.affiliation;
  const editingId = existing?.affiliationId;
  const affiliationType = selectedType(form);

  if (!affiliationTypes[affiliationType]) {
    showFormMessage(form, 'affiliation-required');
    return;
  }

  const fields = collectFields(form, affiliationType);

  if (missingRequired(affiliationType, fields)) {
    showFormMessage(form, 'affiliation-required');
    return;
  }

  const ownCpr = form.querySelector<HTMLInputElement>(`#${ownCprInput}`);
  const ownCprChanged = affiliationTypes[affiliationType].ownCpr
    && (ownCpr?.value.trim() ?? '') !== (ownCpr?.dataset.loaded ?? '');

  const affiliationChanged = !nothingChanged(form, affiliationType, fields, existing);

  if (!ownCprChanged && !affiliationChanged) {
    await options.onSaved();
    dialog?.close();
    return;
  }

  if (affiliationChanged) {
    const invalidCpr = invalidCprField(affiliationType, fields);
    if (invalidCpr) {
      showFieldError(form, fieldInputs[invalidCpr].input);
      return;
    }
  }

  showSpinner();

  try {
    if (affiliationChanged) {
      const limitError = await checkAffiliationLimit(affiliationType, editingId);
      if (limitError) {
        showFormMessage(form, limitError);
        return;
      }
    }

    if (ownCprChanged) {
      const failedInput = await saveOwnCpr(form);
      if (failedInput) {
        showFieldError(form, failedInput);
        return;
      }
    }

    if (affiliationChanged) {
      const documents = await resolveDocuments(form, affiliationType, existing);
      const saved = await submitAffiliation(editingId, affiliationType, { ...fields, ...documents });

      if (!saved) {
        showFormMessage(form, 'affiliation-failed');
        return;
      }
    }

    await options.onSaved();
    dialog?.close();
  } catch (err) {
    console.error('Failed to save affiliation', err);
    showFormMessage(form, errorKey(err));
  } finally {
    hideSpinner();
  }
}

export const openAffiliation = (options: OpenAffiliationOptions) => {
  const form = document.createElement('form');

  openModal({
    name: 'tilknytning',
    insertContent: (d) => {
      const innerHtml = d.innerHTML;
      d.innerHTML = '';

      form.innerHTML = innerHtml;
      form.onsubmit = e => handleSubmit(e as SubmitEvent, form, options, d.closest('dialog'));
      d.appendChild(form);

      setupAffiliationFields(form);
      setupFileInputs(form, options.affiliation);

      if (options.affiliation) {
        const { affiliationType, fields } = options.affiliation;
        fillAffiliation(form, options.affiliation);
        toggleAffiliationFields(form, affiliationType);
        loadAffiliationFields(affiliationType, form, fields.studentProgram).catch(console.error);
      }

      autofillOwnCpr(form)
        .then(() => setupCprValidation(form, ownCprInput))
        .catch(console.error);

      setupCprValidation(form, fieldInputs.memberCprNumber.input);
    },
  });
}
